/**
 * Auth-context wrapper: handles Clerk login → Regta mint → whoami, then
 * keeps the Regta JWT fresh via the module-level token provider.
 * Exposes `{ token, userId, actor, isReady, error }` via context.
 *
 * Token-resolver model: the heavy lifting (in-flight dedupe, freshness
 * check, force-refresh on 401-exp) lives in `token-provider.ts`. This
 * file is just the React glue:
 *
 *   1. Registers a `minter` with the provider on mount. The minter
 *      calls Clerk's `getToken()` (with one retry for the
 *      back-from-background race) and then `mintRegtaJwt()`.
 *   2. Mirrors the provider's session into React state so UI can gate
 *      on `isReady` and render the actor.
 *   3. Schedules a soft proactive refresh ~60s before expiry — purely
 *      a warmth optimisation; the provider itself triggers a refresh
 *      on demand from the very next API call after expiry.
 *
 * The Regta JWT has a 15-min TTL (see auth-backend/mint.ts). Clerk's
 * session is much longer; `getToken()` returns a fresh Clerk JWT we
 * re-feed into `/auth/mint` — no Regta-side refresh token.
 *
 * `userId` (Clerk's stable identity) is the cache-scope key for SWR:
 * it flips on actual sign-in/out, never on token rotation, and is
 * naturally different for a different signed-in user. See query.ts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import { mintRegtaJwt, tenantApi, type Actor } from './api.js';
import { mapAuthError } from './auth-error.js';
import {
  clearSession,
  clearTokenProvider,
  registerTokenMinter,
  type TokenSession,
} from './token-provider.js';

type Ctx = {
  /** Current Regta JWT, or null when signed out / pre-first-mint. */
  token: string | null;
  /** True once we've minted at least once and have an actor. */
  isReady: boolean;
  /** Clerk user id — stable across token rotations. Null when signed out. */
  userId: string | null;
  actor: Actor | null;
  error: string | null;
};

const AuthCtx = createContext<Ctx>({
  token: null,
  isReady: false,
  userId: null,
  actor: null,
  error: null,
});

export function useTenantSession(): Ctx {
  return useContext(AuthCtx);
}

const REFRESH_LEAD_SECONDS = 60;
const MIN_REFRESH_DELAY_MS = 5_000;
const CLERK_RETRY_DELAY_MS = 250;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function TenantSessionProvider({ children }: { children: ReactNode }) {
  const { getToken: clerkGetToken, isSignedIn, userId } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [actor, setActor] = useState<Actor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Stash Clerk's getToken in a ref so the module-level minter closure
  // always reads the latest reference, even across renders.
  const clerkGetTokenRef = useRef(clerkGetToken);
  clerkGetTokenRef.current = clerkGetToken;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleRefresh = useCallback((expiresAt: number) => {
    clearTimer();
    const ms = expiresAt * 1000 - Date.now() - REFRESH_LEAD_SECONDS * 1000;
    const delay = Math.max(ms, MIN_REFRESH_DELAY_MS);
    timerRef.current = setTimeout(() => {
      // Soft proactive refresh — drives the provider's force-refresh
      // path. Swallow errors; the next API call will retry naturally.
      void import('./token-provider.js').then(({ getToken }) =>
        getToken({ force: true }).catch(() => {}),
      );
    }, delay);
  }, []);

  useEffect(() => {
    if (!isSignedIn) {
      clearTimer();
      clearTokenProvider();
      setToken(null);
      setActor(null);
      setIsReady(false);
      return;
    }
    cancelledRef.current = false;

    // Register the minter — called by the provider whenever it needs
    // a fresh Regta JWT (initial load, force-refresh after 401-exp,
    // soft proactive refresh).
    registerTokenMinter(
      async (): Promise<TokenSession> => {
        // Clerk sometimes returns null transiently right after a tab
        // becomes active again. One short retry covers that race; a
        // second null is treated as a real "session inactive" state.
        const fetchClerk = async (): Promise<string | null> =>
          clerkGetTokenRef.current();
        let clerkJwt = await fetchClerk();
        if (!clerkJwt) {
          await sleep(CLERK_RETRY_DELAY_MS);
          clerkJwt = await fetchClerk();
        }
        if (!clerkJwt) {
          const err = new Error('clerk_session_missing');
          throw err;
        }
        const mint = await mintRegtaJwt(clerkJwt);
        return { token: mint.token, expiresAt: mint.expires_at };
      },
      (session) => {
        if (cancelledRef.current) return;
        if (session) {
          setToken(session.token);
          scheduleRefresh(session.expiresAt);
        } else {
          setToken(null);
        }
      },
    );

    // Kick off the initial mint + whoami.
    void (async () => {
      try {
        const { getToken: getRegtaToken } = await import('./token-provider.js');
        const t = await getRegtaToken();
        if (cancelledRef.current) return;
        // Token already in state via onChange; fetch the actor.
        const w = await tenantApi.whoami();
        if (cancelledRef.current) return;
        setActor(w.actor);
        setIsReady(true);
        setError(null);
        // Silence unused-var lint: `t` proves the mint succeeded.
        void t;
      } catch (e) {
        if (!cancelledRef.current) setError(mapAuthError(e));
      }
    })();

    return () => {
      cancelledRef.current = true;
      clearTimer();
      clearSession();
    };
  }, [isSignedIn, scheduleRefresh]);

  return (
    <AuthCtx.Provider value={{ token, isReady, userId: userId ?? null, actor, error }}>
      {children}
    </AuthCtx.Provider>
  );
}
